/**
 * Сохранение/восстановление состояния колонок AG Grid очереди квот в
 * localStorage. Раньше жил инлайном в ProcurementQueuePage.tsx — вынесен
 * 21.05.2026 без изменения поведения.
 *
 * Ключ зависит от userId (`crm_proc_queue_cols_v2_<userId>`) — каждый
 * закупщик настраивает порядок/ширину колонок под себя.
 */
import { useCallback, useRef, useState } from 'react'
import type { GridApi, GridReadyEvent } from 'ag-grid-community'

const COL_STATE_KEY = (userId: string) => `crm_proc_queue_cols_v2_${userId}`

export function useQueueColumnState(userId: string | undefined) {
  const gridApiRef = useRef<GridApi | null>(null)
  const [sortActive, setSortActive] = useState(false)

  const saveState = useCallback(
    (api: GridApi) => {
      if (!userId) return
      try {
        localStorage.setItem(COL_STATE_KEY(userId), JSON.stringify(api.getColumnState()))
      } catch {
        /* ignore */
      }
    },
    [userId],
  )

  const refreshSortActive = useCallback((api: GridApi) => {
    const hasSort = api.getColumnState().some((s) => s.sort != null)
    setSortActive(hasSort)
  }, [])

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApiRef.current = params.api
      if (userId) {
        const saved = localStorage.getItem(COL_STATE_KEY(userId))
        if (saved) {
          try {
            params.api.applyColumnState({ state: JSON.parse(saved), applyOrder: true })
          } catch {
            /* ignore */
          }
        }
      }
      refreshSortActive(params.api)
    },
    [userId, refreshSortActive],
  )

  const resetSort = useCallback(() => {
    const api = gridApiRef.current
    if (!api) return
    // Очищаем только сортировку, порядок/ширина/фильтры не трогаем —
    // после этого AG Grid вернётся к порядку строк из API
    // (горит → приоритет → категория → дедлайн).
    api.applyColumnState({ defaultState: { sort: null } })
    saveState(api)
    refreshSortActive(api)
  }, [saveState, refreshSortActive])

  return { gridApiRef, sortActive, saveState, refreshSortActive, onGridReady, resetSort }
}
